/*
 * Maps all the values of a Record.
 */
export function mapValues<K extends string | number, A, B>(
  record: Readonly<Record<K, A>>,
  mapFunction: (value: A, key: K) => B
): Record<K, B> {
  const result = {} as Record<K, B>

  for (const key in record) {
    result[key] = mapFunction(record[key], key)
  }

  return result
}

export function filterValues<K extends string | number, A, B extends A>(
  record: Readonly<Record<K, A>>,
  filterFunction: (value: A, key: K) => value is B
): Record<K, B>
export function filterValues<K extends string | number, A>(
  record: Readonly<Record<K, A>>,
  filterFunction: (value: A, key: K) => boolean
): Record<K, A>
export function filterValues<K extends string | number, A>(
  record: Readonly<Record<K, A>>,
  filterFunction: (value: A, key: K) => boolean
): Record<K, A> {
  const result = {} as Record<K, A>

  for (const key in record) {
    if (filterFunction(record[key], key)) result[key] = record[key]
  }

  return result
}

/**
 * Removes a key/value from an object and returns a new object (and type)
 */
export function removeKey<T, K extends (keyof T)[]>(
  object: T,
  ...keysToRemove: K
): Omit<T, K[number]> {
  const clone = { ...object }
  keysToRemove.forEach((key) => {
    delete clone[key]
  })
  return clone
}

/**
 * Just like the 'in' operator but actually refines the type of the key.
 */
export function isKeyOf<T extends object>(
  obj: T,
  key: string
): key is string & keyof T {
  return key in obj
}

export function isEmpty(obj: object): boolean {
  return Object.keys(obj).length === 0
}

/**
 * Just like Object.keys but returns the most specific key type.
 */
export function keys<T extends object>(obj: T) {
  return Object.keys(obj) as StringifiedKey<keyof T>[]
}

type StringifiedKey<Key extends string | number | symbol> = Key extends string
  ? Key
  : Key extends number
  ? `${Key}`
  : never
